import { AxiosError, AxiosRequestConfig } from 'axios';
import { ENDPOINTS } from '../http/endpoints';
import { buildV2Url, http } from '../http/http';
import { toMapCardOrgProfileFromPublicRead } from '../utils/mapCardOrgProfile';
import {
  BackfillJobStatus,
  ButtonsOverview,
  GroupSummaryView,
  infinitePagination,
  OrganisationAddressCreateView,
  OrganisationAddressReadView,
  OrganisationAddressUpdateView,
  OrganisationBasicInfoView,
  OrganisationContactCreateView,
  OrganisationContactReadView,
  OrganisationContactUpdateView,
  OrganisationCreateView,
  OrganisationListView,
  OrganisationMapCardCreateView,
  OrganisationMapCardPublicReadView,
  OrganisationMapCardReadView,
  OrgMapCardButtonPublicReadView,
  OrganisationMembershipCreateView,
  OrganisationMembershipReadView,
  OrganisationPublicListItemView,
  OrganisationPublicReadView,
  OrganisationReadView,
  OrganisationReferralLinkCreateView,
  OrganisationReferralLinkReadView,
  OrganisationReferralLinkUpdateView,
  MapCardOrgProfile,
  OrganisationHomeSummaryListView,
  OrganisationSummaryView,
  OrganisationUpdateView,
  OrgNoResult,
  ProviderReadView,
  UserReadView
} from '../types/types';

// GET /v2/organisations — search (q), sort (sortBy/sortDir) and real pagination (limit/offset).
// This one endpoint has moved to v2 while the rest of the Organisation API is still on v1, so it
// builds its own absolute URL rather than going through ENDPOINTS.organisations + the shared /v1/ base.
export const getOrganisations = async (config?: AxiosRequestConfig): Promise<OrganisationListView> => {
  // infinitePagination is only the default: an explicit limit/offset in config.params (e.g. for
  // server-side pagination) must win, so it goes first and gets overridden by the caller's params.
  const { data } = await http.get<OrganisationListView>(buildV2Url('organisations'), {
    ...config,
    params: { ...infinitePagination, ...config?.params }
  });
  return data;
};

export const getPublicOrganisations = async (
  config?: AxiosRequestConfig
): Promise<OrganisationPublicListItemView[]> => {
  const { data } = await http.get<OrganisationPublicListItemView[]>(`${ENDPOINTS.organisations}/public`, {
    ...config,
    params: { ...config?.params, ...infinitePagination }
  });

  return data;
  // await new Promise((resolve) => setTimeout(resolve, 800));
  // return dummyOrganisations;
};

/** Public single-org slim JSON → map-card profile fields. */
export const getPublicOrganisation = async (
  organisationId: number,
  config?: AxiosRequestConfig
): Promise<MapCardOrgProfile> => {
  const { data } = await http.get<OrganisationPublicReadView>(`organisations/public/${organisationId}`, config);
  return toMapCardOrgProfileFromPublicRead(data);
};

export const getOrganisationsSummary = async (
  config?: AxiosRequestConfig
): Promise<OrganisationHomeSummaryListView> => {
  const { data } = await http.get<OrganisationHomeSummaryListView>(ENDPOINTS.organisationsSummary, {
    ...config,
    params: { ...config?.params, ...infinitePagination }
  });
  return data;
};

export const getOrganisationSummary = async (
  id: number,
  config?: AxiosRequestConfig
): Promise<OrganisationSummaryView> => {
  const { data } = await http.get<OrganisationSummaryView>(`${ENDPOINTS.organisationsSummary}/${id}`, {
    ...config
  });
  return data;
};

export const createOrganisation = async (organisation: OrganisationCreateView): Promise<OrganisationReadView> => {
  const { data } = await http.post<OrganisationReadView>(ENDPOINTS.organisations, organisation);
  return data;
  //await new Promise((resolve) => setTimeout(resolve, 800));
  // return dummyOrganisations.records[0];
};

export const getBasicOrganisationInfo = async (
  id: number,
  config?: AxiosRequestConfig
): Promise<OrganisationBasicInfoView> => {
  const { data } = await http.get<OrganisationBasicInfoView>(`${ENDPOINTS.organisationsInfo}/${id}`, config);
  return data;
};

export const getOrganisationById = async (id: string, config?: AxiosRequestConfig): Promise<OrganisationReadView> => {
  const { data } = await http.get<OrganisationReadView>(`${ENDPOINTS.organisations}/${id}`, config);
  return data;
  //  await new Promise((resolve) => setTimeout(resolve, 800));
  // return dummyOrganisations.records.find((r) => r.id === Number(id)) ?? dummyOrganisations.records[0];
};

/** Download single-organisation CSV (GET /v1/export/organisations/{organisationId}/single). Returns blob. */
export const downloadOrganisationCsv = async (organisationId: number, config?: AxiosRequestConfig): Promise<Blob> => {
  const response = await http.get(`${ENDPOINTS.exportOrganisations}/${organisationId}/single`, {
    ...config,
    responseType: 'blob'
  });
  return response.data as Blob;
};

export const updateOrganisation = async (
  id: string | number,
  organisation: OrganisationUpdateView
): Promise<OrganisationReadView> => {
  const { data } = await http.patch<OrganisationReadView>(`${ENDPOINTS.organisations}/${id}`, organisation);
  return data;
  // await new Promise((resolve) => setTimeout(resolve, 800));
  // return dummyOrganisations.records[0];
};

export const deleteOrganisation = async (id: string | number): Promise<number> => {
  const { status } = await http.delete(`${ENDPOINTS.organisations}/${id}`);
  return status;
  // await new Promise((resolve) => setTimeout(resolve, 800));
  // return 200;
};

export const deleteUserFromOrganisation = async (orgId: string | number, userId: string | number): Promise<number> => {
  const { status } = await http.delete(`${ENDPOINTS.organisations}/${orgId}/users/${userId}`);
  return status;
  // await new Promise((resolve) => setTimeout(resolve, 800));
  // return 200;
};

export const updateUserInOrganisation = async (
  orgId: string | number,
  userId: string | number
): Promise<UserReadView> => {
  const { data } = await http.put<UserReadView>(`${ENDPOINTS.organisations}/${orgId}/users/${userId}`);
  return data;
  // await new Promise((resolve) => setTimeout(resolve, 800));
  // return { email: 'SummyUser@test.com', id: 111, isSuperUser: false };
};

export const addUserToOrganisation = async (
  orgId: string | number,
  userId: string | number,
  payload: OrganisationMembershipCreateView
): Promise<OrganisationMembershipReadView> => {
  const { data } = await http.put<OrganisationMembershipReadView>(
    `${ENDPOINTS.organisations}/${orgId}/users/${userId}`,
    payload
  );
  return data;
  // await new Promise((resolve) => setTimeout(resolve, 800));
  // return { email: 'SummyUser@test.com', id: 111, isSuperUser: false };
};

// --- allow-lists ---
// New in the organisation-self-service model: which groups/providers an organisation's own admin
// (as opposed to a superuser) is allowed to attach/assign. GET works for a superuser or an org
// admin of orgId; PUT (superuser only) always replaces the whole list, so callers must send the
// complete set — [] means nothing allowed.

export const getAllowedGroups = async (orgId: string | number, config?: AxiosRequestConfig): Promise<GroupSummaryView[]> => {
  const { data } = await http.get<GroupSummaryView[]>(buildV2Url(`${ENDPOINTS.organisations}/${orgId}/allowed-groups`), config);
  return data;
};

export const updateAllowedGroups = async (orgId: string | number, groupIds: number[]): Promise<GroupSummaryView[]> => {
  const { data } = await http.put<GroupSummaryView[]>(buildV2Url(`${ENDPOINTS.organisations}/${orgId}/allowed-groups`), {
    groupIds
  });
  return data;
};

export const getAllowedProviders = async (orgId: string | number, config?: AxiosRequestConfig): Promise<ProviderReadView[]> => {
  const { data } = await http.get<ProviderReadView[]>(buildV2Url(`${ENDPOINTS.organisations}/${orgId}/allowed-providers`), config);
  return data;
};

export const updateAllowedProviders = async (orgId: string | number, providerIds: number[]): Promise<ProviderReadView[]> => {
  const { data } = await http.put<ProviderReadView[]>(buildV2Url(`${ENDPOINTS.organisations}/${orgId}/allowed-providers`), {
    providerIds
  });
  return data;
};

// --- addresses ---

export const setActiveAddress = async (addressId: number, active: boolean) => {
  const { data } = await http.patch<OrganisationAddressReadView>(`organisation-address/${addressId}`, { active });
  return data;
};

export const listOrgAddresses = async (orgId: number) => {
  const { data } = await http.get<OrganisationAddressReadView[]>(`organisation-address/byorg/${orgId}`, {});

  return data;
};

export const getOrgAddressById = async (addressId: number) => {
  const { data } = await http.get<OrganisationAddressReadView>(`organisation-address/${addressId}`, {});
  return data;
};

export const postAddress = async (orgId: number, body: OrganisationAddressCreateView) => {
  const { data } = await http.post<OrganisationAddressReadView>(`organisation-address/byorg/${orgId}`, body);
  return data;
};

export const patchAddress = async (addressId: number, body: OrganisationAddressUpdateView) => {
  const { data } = await http.patch<OrganisationAddressReadView>(`organisation-address/${addressId}`, body);
  return data;
};

export const deleteAddress = async (addressId: number) =>
  http.delete(`organisation-address/${addressId}`).then((r) => r);

// --- contacts ---
export const listOrgContacts = async (orgId: number) => {
  const { data } = await http.get<OrganisationContactReadView[]>(`organisation-contact/${orgId}`, {});
  return data;
};

export const postContact = async (orgId: number, body: OrganisationContactCreateView) => {
  const { data } = await http.post<OrganisationContactReadView>(`organisation-contact/${orgId}`, body);
  return data;
};

export const patchContact = async (id: number, body: OrganisationContactUpdateView) => {
  const { data } = await http.patch<OrganisationContactReadView>(`organisation-contact/${id}`, body);
  return data;
};

export const deleteContact = (id: number) => http.delete(`organisation-contact/${id}`).then((r) => r);

// --- referral links ---
export const listOrgReferralLinks = async (orgId: number) => {
  const { data } = await http.get<OrganisationReferralLinkReadView[]>(
    `organisation-referral-link/by-organisation/${orgId}`,
    {}
  );
  return data;
};

export const postReferralLink = async (orgId: number, body: OrganisationReferralLinkCreateView) => {
  const { data } = await http.post<OrganisationReferralLinkReadView>(
    `organisation-referral-link/by-organisation/${orgId}`,
    body
  );
  return data;
};

export const patchReferralLink = async (id: number, body: OrganisationReferralLinkUpdateView) => {
  const { data } = await http.patch<OrganisationReferralLinkReadView>(`organisation-referral-link/${id}`, body);
  return data;
};

export const deleteReferralLink = async (id: number) => http.delete(`organisation-referral-link/${id}`).then((r) => r);

export const getOrganisationMapCardInfo = async (orgId: number): Promise<OrganisationMapCardReadView | null> => {
  await new Promise((resolve) => setTimeout(resolve, 800));
  return null;
};

export const getMapCardByOrg = async (orgId: number) => {
  const { data } = await http.get<OrganisationMapCardReadView>(`org-map-card/by-organisation/${orgId}`);
  return data;
};

/** Public map info box — slim read model; admin/edit flows should use {@link getMapCardByOrg}. */
export const getPublicMapCardByOrg = async (orgId: number) => {
  const { data } = await http.get<OrganisationMapCardPublicReadView>(`org-map-card/public/by-organisation/${orgId}`);
  return data;
};

export const getMapCardById = async (id: number) => {
  const { data } = await http.get<OrganisationMapCardReadView>(`org-map-card/${id}`);
  return data;
};

export const createMapCard = async (orgId: number, body: OrganisationMapCardCreateView) => {
  const { data } = await http.post<OrganisationMapCardReadView>(`org-map-card/by-organisation/${orgId}`, body);
  return data;
};

export const updateMapCard = async (id: number, body: OrganisationMapCardCreateView) => {
  const { data } = await http.patch<OrganisationMapCardReadView>(`org-map-card/${id}`, body);
  return data;
};

export const deleteMapCard = async (id: number) => {
  await http.delete(`org-map-card/${id}`);
};

export const listButtonsOverview = async (config?: AxiosRequestConfig): Promise<ButtonsOverview> => {
  // Use Java Integer.MAX_VALUE (2147483647) since backend accepts int, not long
  const MAX_INT_VALUE = 2147483647;
  const { data } = await http.get<ButtonsOverview>('org-map-card/buttons-overview', {
    ...config,
    params: { ...config?.params, limit: MAX_INT_VALUE, offset: 0 }
  });
  return data;
};

// --- organisation number validation ---

export const validateOrgNumber = async (
  country: string | null,
  value: string,
  config?: AxiosRequestConfig
): Promise<OrgNoResult> => {
  const params: Record<string, string> = { value };
  if (country) {
    params.country = country;
  }
  const { data } = await http.get<OrgNoResult>('validation/org-number', {
    ...config,
    params: { ...(config?.params ?? {}), ...params }
  });
  return data;
};

interface BackfillJobStartResponse {
  jobId: string;
  queuedAt: string; // ISO 8601 timestamp
  message: string;
}

interface BackfillJobConflictResponse {
  code: 409;
  message: string;
  jobId?: string; // Optional - may be null if unavailable
}

export const triggerBackfillKeywords = async (
  batchSize?: number,
  startAfterId?: number,
  maxBatches?: number,
  dryRun?: boolean
): Promise<string> => {
  const params: Record<string, string | number | boolean> = {};
  if (batchSize !== undefined) params.batchSize = batchSize;
  if (startAfterId !== undefined) params.startAfterId = startAfterId;
  if (maxBatches !== undefined) params.maxBatches = maxBatches;
  if (dryRun !== undefined) params.dryRun = dryRun;

  try {
    const { data } = await http.post<BackfillJobStartResponse>('admin/tasks/backfill-keywords', null, {
      params
    });
    return data.jobId;
  } catch (e) {
    const err = e as AxiosError<BackfillJobConflictResponse>;
    if (err.response?.status === 409) {
      const jobId = err.response.data?.jobId;
      if (jobId) {
        // Job already running - return the existing job ID so caller can attach to it
        return jobId;
      }
      // No jobId available - throw error with message (frontend will show toast)
      throw new Error(err.response.data?.message || 'Backfill already running');
    }
    throw e;
  }
};

export const getBackfillJobStatus = async (jobId: string): Promise<BackfillJobStatus> => {
  try {
    const { data } = await http.get<BackfillJobStatus>(`admin/tasks/status/${jobId}`);
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 404) {
      // Job not found - could be due to server restart, cleanup, or invalid jobId
      throw new Error(`Job status unavailable (server may have restarted or job was cleaned up): ${jobId}`);
    }
    throw error;
  }
};

export const pollBackfillJobStatus = async (
  jobId: string,
  onStatusChange?: (status: BackfillJobStatus) => void,
  pollInterval: number = 2000,
  maxAttempts: number = 300 // 10 minutes max (300 * 2s)
): Promise<BackfillJobStatus> => {
  let attempts = 0;

  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        const status = await getBackfillJobStatus(jobId);

        if (onStatusChange) {
          onStatusChange(status);
        }

        if (status.state === 'SUCCESS' || status.state === 'FAILED') {
          resolve(status);
          return;
        }

        attempts++;
        if (attempts >= maxAttempts) {
          reject(new Error('Polling timeout: job did not complete within expected time'));
          return;
        }

        setTimeout(poll, pollInterval);
      } catch (error) {
        reject(error);
      }
    };

    poll();
  });
};

export const getButtonById = async (
  buttonId: number,
  includeUrls: boolean = true
): Promise<OrgMapCardButtonPublicReadView> => {
  const { data } = await http.get<OrgMapCardButtonPublicReadView>(`org-map-card/button/${buttonId}`, {
    params: { includeUrls }
  });
  return data;
};

export const getButtonsByIds = async (
  buttonIds: number[],
  includeUrls: boolean = true
): Promise<OrgMapCardButtonPublicReadView[]> => {
  const idsParam = buttonIds.join(',');
  const { data } = await http.get<OrgMapCardButtonPublicReadView[]>('org-map-card/buttons', {
    params: { ids: idsParam, includeUrls }
  });
  return data;
};

/* const dummyOrganisations: OrganisationListView = {
  limit: Number.MAX_SAFE_INTEGER,
  offset: 0,
  totalCount: 35,
  records: Array.from({ length: 35 }, () => {
    const id = Math.floor(Math.random() * 1000);
    return { id, name: 'OrgName' + id, identificationNumber: '556588-2965', lat: 57.8558, lng: 14.7584 };
  })
}; */
